import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/wolfApi";

export default function Fire() {
  const [pages, setPages] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/fire-pages")
      .then((r) => setPages(r.data))
      .catch((e) => setErr(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const open = async (id) => {
    try {
      const r = await api.get(`/fire-pages/${id}`);
      setActive(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { setErr(e.message); }
  };

  if (active) {
    return (
      <article className="pt-32 sm:pt-40 pb-28 bg-[#080808] min-h-screen" data-testid="fire-reader">
        <div className="max-w-3xl mx-auto px-6 sm:px-10">
          <button
            onClick={() => setActive(null)}
            data-testid="fire-back-btn"
            className="text-[11px] uppercase tracking-[0.32em] text-white/60 hover:text-[#D4AF37] inline-flex items-center gap-2 mb-12"
          >
            <ArrowLeft size={14}/> Back to Fire Pages
          </button>
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]/90 mb-4">{active.kind}</p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-display text-6xl sm:text-8xl text-white leading-[0.9]"
            data-testid="fire-reader-title"
          >
            {active.title}
          </motion.h1>
          <p className="font-editorial italic text-white/60 text-xl sm:text-2xl mt-6 leading-snug">
            {active.excerpt}
          </p>
          <div className="mt-14 space-y-7">
            {active.body.map((p, i) => (
              <motion.p
                key={`${active.id}-${i}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.05 }}
                className="font-editorial text-white/85 text-lg sm:text-xl leading-[1.85]"
              >
                {p}
              </motion.p>
            ))}
          </div>
          <div className="mt-20 border-t border-white/10 pt-10">
            <p className="font-display tracking-extreme text-white/40 text-sm">— WOLF · MARK LESLIE</p>
          </div>
        </div>
      </article>
    );
  }

  return (
    <div className="pt-32 sm:pt-40 pb-20 bg-[#080808] min-h-screen" data-testid="fire-page">
      <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16">
        <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-6">— Fire Pages</p>
        <h1 className="font-display wolf-gold-foil text-6xl sm:text-8xl lg:text-9xl leading-[0.85]">Fire Pages</h1>
        <p className="font-editorial italic text-xl sm:text-2xl text-white/55 mt-6 max-w-2xl">
          Short powerful writings. Poetry. Memoir. The atmosphere behind the records.
        </p>

        {loading && <p className="mt-20 text-white/40 font-editorial italic">Tracking…</p>}
        {err && <p className="mt-20 text-red-300" data-testid="fire-error">{err}</p>}

        <div className="mt-20 grid grid-cols-1 gap-0 border-t border-white/10">
          {pages.map((p, i) => (
            <motion.button
              key={p.id}
              data-testid={`fire-item-${p.id}`}
              onClick={() => open(p.id)}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="group text-left border-b border-white/10 py-10 sm:py-14 hover:bg-white/[0.02] hover:pl-3 transition-all duration-500"
            >
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]/80">{p.kind}</p>
                  <h3 className="font-display text-4xl sm:text-6xl text-white mt-3 group-hover:text-[#D4AF37] transition-colors">
                    {p.title}
                  </h3>
                </div>
                <p className="text-[10px] uppercase tracking-[0.32em] text-white/40 group-hover:text-white">
                  Read →
                </p>
              </div>
              <p className="font-editorial italic text-white/55 mt-4 max-w-3xl">{p.excerpt}</p>
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
}
